import { useEffect } from "react";
import { Button, Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { convertDoubleToCurrencyPTBR } from "../../../../../utils/ConverterUtils";
import AsteriskRequired from "../../../Common/AsteriskRequired";
import InputNumeric from "../../../Forms/Element/InputNumeric";
import InputText from "../../../Forms/Element/InputText";
import SegmentedButtons from "../../../Forms/Element/SegmentedButtons";
import BranchSelect from "../../Main/Branch/BranchSelect";
import EmployeeSelect from "../../Main/Employee/EmployeeSelect";
import ProductSelect from "../../Main/Product/ProductSelect";
import PaymentMethodSelect from "../../Common/PaymentMethod/PaymentMethodSelect";
import EmployeeConsumptionPriceType, { hasCostPrice, resolveItemPriceType } from "./EmployeeConsumptionPriceType";
import { EmployeeConsumptionSettlement, newConsumptionItem } from "./useEmployeeConsumptionForm";
import "./EmployeeConsumptionForm.css";

const formatCurrency = convertDoubleToCurrencyPTBR;

/** Mensagem de validação abaixo do campo, no mesmo estilo do formulário de vales. */
const FieldError = ({ message }) => message
    ? <div className="text-danger fs-12 mt-1">{message}</div>
    : null;

/**
 * Campos do lançamento de consumo do colaborador.
 *
 * Não tem estado nem envio próprios: recebe o formik de useEmployeeConsumptionForm. É o mesmo
 * componente na página Consumo da equipe > Novo e no modal da tela de Caixas, para os dois
 * nunca divergirem.
 *
 * @param {object} formik - Formik devolvido por useEmployeeConsumptionForm.
 * @param {boolean} isBranchFixed - Filial travada pela seleção do topo do sistema.
 */
const EmployeeConsumptionForm = ({ formik, isBranchFixed }) => {

    const { t } = useTranslation();
    const { values, errors, setFieldValue } = formik;

    const items = values.items || [];
    const itemsWithProduct = items.filter(item => item.product);

    // No lançamento pelo custo, o produto sem custo cadastrado vai pelo preço normal e os demais
    // seguem pelo custo (resolveItemPriceType). "Preço de custo" só deixa de servir quando
    // nenhum produto escolhido tem custo; aí o preço volta sozinho para o normal.
    const anyItemHasCost = itemsWithProduct.some(item => hasCostPrice(item.product));
    const costUnavailable = itemsWithProduct.length > 0 && !anyItemHasCost;
    useEffect(() => {
        if (costUnavailable && values.priceType === EmployeeConsumptionPriceType.COST) {
            setFieldValue('priceType', EmployeeConsumptionPriceType.NORMAL);
        }
    }, [costUnavailable, values.priceType, setFieldValue]);

    const unitPriceOf = (item, priceType) => {
        if (!item.product) return 0;
        return resolveItemPriceType(priceType, item.product) === EmployeeConsumptionPriceType.COST
            ? Number(item.product.costPrice || 0)
            : Number(item.product.price || 0);
    };
    // Item que vai pelo preço normal num lançamento pelo custo: a linha avisa, para o operador
    // não achar que o custo foi ignorado.
    const isNormalInCostLaunch = (item) => Boolean(item.product)
        && values.priceType === EmployeeConsumptionPriceType.COST
        && !hasCostPrice(item.product);
    const subtotalOf = (item, priceType = values.priceType) =>
        Math.round(unitPriceOf(item, priceType) * (Number(item.quantity) || 0) * 100) / 100;
    const totalAt = (priceType) => items.reduce((sum, item) => sum + subtotalOf(item, priceType), 0);

    // Prévia para o operador conferir antes de salvar. O valor gravado é recalculado no
    // backend com o preço do cadastro naquele instante.
    const total = totalAt(values.priceType);

    const updateItem = (index, changes) => {
        setFieldValue('items', items.map((item, position) => (position === index ? { ...item, ...changes } : item)));
    };
    const changeQuantity = (index, delta) => {
        const current = Number(items[index].quantity) || 0;
        updateItem(index, { quantity: String(Math.max(1, current + delta)) });
    };
    const addItem = () => setFieldValue('items', [...items, newConsumptionItem()]);
    const removeItem = (index) => setFieldValue('items', items.filter((_, position) => position !== index));

    // O erro de um item chega como lista (um objeto por linha); o de "nenhum item", como texto.
    const itemError = (index, field) => Array.isArray(errors.items) ? errors.items[index]?.[field] : null;
    const listError = typeof errors.items === 'string' ? errors.items : null;

    return (
        <Row className="g-3 employee-consumption-form">
            <Col md={6}>
                <FormGroup>
                    <FormLabel>{t('branch')} <AsteriskRequired /></FormLabel>
                    {/* Com filial no topo o campo vem travado: quem opera uma unidade não deveria
                        baixar o estoque de outra. */}
                    <BranchSelect
                        value={values.branch}
                        onChange={(item) => {
                            setFieldValue('branch', item);
                            // O colaborador escolhido pode não trabalhar na filial nova.
                            setFieldValue('employee', null);
                        }}
                        isDisabled={isBranchFixed}
                    />
                    <FieldError message={errors.branch} />
                </FormGroup>
            </Col>
            <Col md={6}>
                <FormGroup>
                    <FormLabel>{t('employee')} <AsteriskRequired /></FormLabel>
                    {/* Todos os colaboradores da filial, não só quem atende: a recepção também
                        consome. A key recarrega a lista quando a filial muda. */}
                    <EmployeeSelect
                        key={values.branch?.value ?? 'all'}
                        branch={values.branch}
                        filterProvidesService={false}
                        value={values.employee}
                        onChange={(item) => setFieldValue('employee', item)}
                    />
                    <FieldError message={errors.employee} />
                </FormGroup>
            </Col>
            <Col md={12}>
                <FormLabel>{t('cash_flow.consumption_items')} <AsteriskRequired /></FormLabel>
                {/* Uma linha por produto. No celular a linha quebra em duas: produto em cima,
                    quantidade e subtotal embaixo, com botões grandes para o toque. */}
                <div className="employee-consumption-form__items">
                    {items.map((item, index) => (
                        <div className="employee-consumption-form__item" key={item.key}>
                            <div className="employee-consumption-form__item-product">
                                {/* Só produto: serviço não se consome, e o backend recusa. */}
                                <ProductSelect
                                    type="PRODUCT"
                                    value={item.product}
                                    onChange={(option) => updateItem(index, { product: option })}
                                />
                                <FieldError message={itemError(index, 'product')} />
                                {isNormalInCostLaunch(item) && (
                                    <small className="text-muted d-block mt-1">
                                        <i className="fa-solid fa-circle-info me-1" aria-hidden="true" />
                                        {t('cash_flow.consumption_item_no_cost_normal_price')}
                                    </small>
                                )}
                            </div>
                            <div className="employee-consumption-form__item-controls">
                                <div className="employee-consumption-form__stepper">
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => changeQuantity(index, -1)}
                                        disabled={(Number(item.quantity) || 0) <= 1}
                                        aria-label={t('decrease')}
                                    >
                                        <i className="fa-solid fa-minus" aria-hidden="true" />
                                    </Button>
                                    <InputNumeric
                                        name={`items.${index}.quantity`}
                                        value={item.quantity}
                                        onChange={(event) => updateItem(index, { quantity: event.target.value })}
                                        className="text-center"
                                        aria-label={t('quantity')}
                                    />
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => changeQuantity(index, 1)}
                                        aria-label={t('increase')}
                                    >
                                        <i className="fa-solid fa-plus" aria-hidden="true" />
                                    </Button>
                                </div>
                                <strong className="employee-consumption-form__item-subtotal">
                                    {formatCurrency(subtotalOf(item))}
                                </strong>
                                {/* Com um item só não há o que remover: o botão some para a linha
                                    não ficar vazia sem jeito de voltar. */}
                                {items.length > 1 && (
                                    <Button
                                        variant="link"
                                        className="employee-consumption-form__item-remove"
                                        onClick={() => removeItem(index)}
                                        aria-label={t('remove')}
                                        title={t('remove')}
                                    >
                                        <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                    </Button>
                                )}
                            </div>
                            <FieldError message={itemError(index, 'quantity')} />
                        </div>
                    ))}
                </div>
                <FieldError message={listError} />
                <Button variant="outline-primary" className="employee-consumption-form__add-item mt-2" onClick={addItem}>
                    <i className="fa-solid fa-plus me-2" aria-hidden="true" />
                    {t('cash_flow.consumption_add_item')}
                </Button>
            </Col>
            <Col md={6}>
                <FormGroup>
                    <FormLabel>{t('cash_flow.consumption_price')}</FormLabel>
                    {/* Cada opção mostra o total do lançamento naquele preço, para o operador ver
                        quanto sai antes de escolher. */}
                    <SegmentedButtons
                        ariaLabel={t('cash_flow.consumption_price')}
                        value={values.priceType}
                        onChange={(value) => setFieldValue('priceType', value)}
                        options={[
                            {
                                value: EmployeeConsumptionPriceType.NORMAL,
                                label: t('cash_flow.consumption_price_normal'),
                                hint: itemsWithProduct.length ? formatCurrency(totalAt(EmployeeConsumptionPriceType.NORMAL)) : null
                            },
                            {
                                value: EmployeeConsumptionPriceType.COST,
                                label: t('cash_flow.consumption_price_cost'),
                                hint: itemsWithProduct.length
                                    ? (costUnavailable ? t('cash_flow.consumption_no_cost') : formatCurrency(totalAt(EmployeeConsumptionPriceType.COST)))
                                    : null,
                                disabled: costUnavailable
                            }
                        ]}
                    />
                </FormGroup>
            </Col>
            <Col md={6}>
                <FormGroup>
                    <FormLabel>{t('cash_flow.consumption_settlement')}</FormLabel>
                    <SegmentedButtons
                        ariaLabel={t('cash_flow.consumption_settlement')}
                        value={values.settlement}
                        onChange={(value) => {
                            setFieldValue('settlement', value);
                            // Voltar para "descontar" limpa a forma de pagamento, que só vale ao pagar.
                            if (value === EmployeeConsumptionSettlement.DISCOUNT) {
                                setFieldValue('paymentMethod', null);
                            }
                        }}
                        options={[
                            { value: EmployeeConsumptionSettlement.DISCOUNT, label: t('cash_flow.consumption_settlement_discount') },
                            { value: EmployeeConsumptionSettlement.PAY_NOW, label: t('cash_flow.consumption_settlement_pay_now') }
                        ]}
                    />
                </FormGroup>
            </Col>
            {values.settlement === EmployeeConsumptionSettlement.PAY_NOW && (
                <Col md={12}>
                    <FormGroup>
                        <FormLabel>{t('payment_method')} <AsteriskRequired /></FormLabel>
                        <PaymentMethodSelect
                            value={values.paymentMethod}
                            onChange={(item) => setFieldValue('paymentMethod', item)}
                            hideSubscription
                        />
                        <FieldError message={errors.paymentMethod} />
                        <small className="text-muted d-block mt-1">
                            {t('cash_flow.consumption_pay_now_hint')}
                        </small>
                    </FormGroup>
                </Col>
            )}
            <Col md={12}>
                <FormGroup>
                    <FormLabel>{t('description')}</FormLabel>
                    <InputText
                        name="description"
                        maxLength={255}
                        value={values.description}
                        onChange={(event) => setFieldValue('description', event.target.value)}
                    />
                    <FieldError message={errors.description} />
                </FormGroup>
            </Col>
            <Col md={12}>
                <div className="employee-consumption-form__total">
                    <span>{t('total')}</span>
                    <strong>{formatCurrency(total)}</strong>
                </div>
            </Col>
        </Row>
    );
};

export default EmployeeConsumptionForm;
